"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurSartorialJourney() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="380" id="our-sartorial-journey" className="dark bg-background text-foreground relative overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-screen lg:min-h-[90vh]">
        {}
        <div ref={leftRef} className={`order-2 lg:order-1 lg:col-span-5 relative flex flex-col justify-center px-6 sm:px-10 lg:px-14 py-16 lg:py-20 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div aria-hidden="true" className="pointer-events-none absolute -top-20 -left-20 w-72 h-72 rounded-lg opacity-20 blur-3xl" style={{
          background: "radial-gradient(circle at center, var(--accent), transparent 70%)"
        }} />

          {}
          <div className="relative z-10 mb-8 lg:mb-10">
            <span className="font-default text-[11px] sm:text-xs tracking-[0.25em] uppercase font-medium" style={{
            color: "var(--accent)"
          }}>
              Heritage Excellence · The Digital Atelier
            </span>
            <span className="inline-block w-12 h-px ml-4 align-middle" style={{
            background: "var(--accent)"
          }} />
          </div>

          {}
          <h1 className="font-default font-black text-foreground relative z-10" style={{
          fontSize: "clamp(3.5rem, 8vw, 7.5rem)",
          lineHeight: "0.85",
          letterSpacing: "-0.05em",
          marginBottom: "0.5rem"
        }}>
            Avant-garde
            <br />
            Couture,
            <br />
            <em className="not-italic" style={{
            color: "var(--accent)"
          }}>
              Redefined
            </em>
            .
          </h1>

          {}
          <p className="mt-8 lg:mt-10 text-base sm:text-lg leading-relaxed text-foreground/75 max-w-[380px] relative z-10" style={{
          fontFamily: "var(--font-sans)"
        }}>
            Bold silhouettes and uncompromising attitude. Experience the definitive intersection of high-street edge and editorial luxury at Drip District.
          </p>

          {}
          <div className="mt-10 lg:mt-12 flex flex-col sm:flex-row gap-4 relative z-10">
            <Button asChild size="lg" className="px-8 h-14 bg-accent text-accent-foreground hover:bg-accent/90 font-default tracking-widest shadow-lg">
              <Link to="/ShopPage">SHOP THE ATELIER</Link>
            </Button>

            <Button asChild variant="ghost" size="lg" className="group px-8 h-14 border border-accent bg-transparent hover:bg-accent/10 text-foreground font-default tracking-widest">
              <Link to="/BrandStoryPage">
                THE HERITAGE
                <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
            </Button>
          </div>

          {}
          <div className="mt-14 lg:mt-20 flex items-end gap-3 relative z-10">
            <svg width="44" height="56" viewBox="0 0 44 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="opacity-60 shrink-0">
              <path d="M22 54 C 22 40, 22 26, 22 4" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
              <path d="M22 14 C 14 12, 8 14, 6 20 C 12 22, 18 20, 22 16" stroke="currentColor" strokeWidth="1" strokeLinecap="round" fill="none" />
              <path d="M22 24 C 30 22, 36 24, 38 30 C 32 32, 26 30, 22 26" stroke="currentColor" strokeWidth="1" strokeLinecap="round" fill="none" />
              <path d="M22 36 C 16 34, 10 36, 9 41 C 14 43, 19 41, 22 38" stroke="currentColor" strokeWidth="1" strokeLinecap="round" fill="none" />
            </svg>
            <p className="text-sm text-foreground/55 leading-snug max-w-[220px]" style={{
            fontFamily: "var(--font-mono)"
          }}>
              LSH-FA / 48.8566° N, 2.3522° E
            </p>
          </div>
        </div>

        {}
        <div ref={rightRef} className={`order-1 lg:order-2 lg:col-span-7 relative min-h-[60vh] lg:min-h-full overflow-hidden transition-all duration-1000 ease-out ${rightVisible ? "opacity-100 scale-100" : "opacity-0 scale-105"}`}>
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/luxury_atelier_editorial_noir.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/luxury_atelier_editorial_noir.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/luxury_atelier_editorial_noir.webp-wvc-sizes" alt="High-contrast editorial fashion photography showcasing luxury atelier craftsmanship" loading="eager" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
          {}
          <div aria-hidden="true" className="absolute inset-0" style={{
          background: "linear-gradient(to right, var(--background) 0%, transparent 20%, transparent 80%, var(--background) 100%), linear-gradient(to bottom, transparent 70%, var(--background) 100%)"
        }} />
        </div>

        {}
        <div className={`hidden lg:flex absolute z-20 top-1/2 -translate-y-1/2 items-center justify-center transition-all duration-1000 delay-300 ${leftVisible ? "opacity-100 rotate-[-8deg] scale-100" : "opacity-0 rotate-0 scale-75"}`} style={{
        left: "41.666%",
        transform: "translate(-50%, -50%) rotate(-8deg)"
      }}>
          <div className="relative w-32 h-32 xl:w-36 xl:h-36 flex items-center justify-center shadow-lg" style={{
          background: "var(--accent)"
        }}>
            {}
            <div className="absolute inset-2 border border-foreground/30" />
            <div className="text-center px-4">
              <p className="text-[10px] xl:text-[11px] leading-tight" style={{
              color: "var(--background)",
              fontFamily: "var(--font-mono)"
            }}>
                NEW
                <br />
                SEASON
                <br />
                2024
              </p>
            </div>
          </div>
        </div>

        {}
        <div className="lg:hidden absolute z-20 top-6 right-6">
          <div className="relative w-24 h-24 flex items-center justify-center shadow-lg rotate-[-8deg]" style={{
          background: "var(--accent)"
        }}>
            <div className="absolute inset-1.5 border border-foreground/30" />
            <div className="text-center px-2">
              <p className="text-[9px] leading-tight" style={{
              color: "var(--background)",
              fontFamily: "var(--font-mono)"
            }}>
                NEW
                <br />
                SEASON
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}